import React, { Suspense, useEffect } from "react";
import { Route, Switch, Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import AppLayout from "layouts/app-layout";
import AuthLayout from "layouts/auth-layout";
import { AssinaturaViews } from "views/assinatura-views";
import AppLocale from "lang";
import { IntlProvider } from "react-intl";
import { ConfigProvider } from "antd";
import { APP_PREFIX_PATH, AUTH_PREFIX_PATH } from "configs/AppConfig";
import useBodyClass from "hooks/useBodyClass";
import Loading from "components/atom/Loading/Loading";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import InvestidorViews from "views/investidor-views";
import VendedorViews from "views/vendedor-views";
import ConsumidorViews from "views/consumidor-views";
import ComercializadoraViews from "views/comercializadora-views";
import ParceiroViews from "views/parceiro-views";
import { Layout } from "antd";
import JwtAuthService from "services/JwtAuthService";
import MenuService from "services/MenuService";

const { Content } = Layout;

const GRUPO_COMERCIALIZADORA = "Comercializadora";
const GRUPO_PARCEIRO = "Parceiro";
const GRUPO_INVESTIDOR = "Investidor";
const GRUPO_VENDEDOR = "Vendedor Ativos";
const GRUPO_CONSUMIDOR = "Consumidor";
const PAINEL_COMERCIALIZADORA_PATH = `${APP_PREFIX_PATH}/painel-comercializadora`;
const PAINEL_PARCEIRO_PATH = `${APP_PREFIX_PATH}/painel-parceiro`;
const INVESTIDOR_MARKETPLACE_PATH = `${APP_PREFIX_PATH}/investidor/marketplace`;
const VENDEDOR_MEUS_ATIVOS_PATH = `${APP_PREFIX_PATH}/vendedor/meus-ativos`;
const CONSUMIDOR_DASHBOARD_PATH = `${APP_PREFIX_PATH}/consumidor/dashboard`;

function RouteInterceptor({ children, isAuthenticated, ...rest }) {
	return (
		<Route
			{...rest}
			render={({ location }) =>
				isAuthenticated ? (
					children
				) : (
					<Redirect
						to={{
							pathname: AUTH_PREFIX_PATH,
							state: { from: location },
						}}
					/>
				)
			}
		/>
	);
}

export const Views = (props) => {
	const { locale, token, location, direction, user } = props;
	const isLoggedIn = Boolean(token);
	const currentAppLocale = AppLocale[locale];
	useBodyClass(`dir-${direction}`);
	const history = useHistory();

	const isComercializador =
		user?.grupo_usuario?.descricao === GRUPO_COMERCIALIZADORA;
	const isParceiro = user?.grupo_usuario?.descricao === GRUPO_PARCEIRO;
	const isInvestidor =
		user?.grupo_usuario?.descricao === GRUPO_INVESTIDOR;
	const isVendedor =
		user?.grupo_usuario?.descricao === GRUPO_VENDEDOR;
	const isOnPainelComercializadora =
		location.pathname === PAINEL_COMERCIALIZADORA_PATH;
	const isOnPainelParceiro = location.pathname === PAINEL_PARCEIRO_PATH;
	const isConsumidor =
		user?.grupo_usuario?.descricao === GRUPO_CONSUMIDOR;
	const isOnInvestidorArea =
		location.pathname.startsWith(`${APP_PREFIX_PATH}/investidor`);
	const isOnVendedorArea =
		location.pathname.startsWith(`${APP_PREFIX_PATH}/vendedor`);
	const isOnConsumidorArea =
		location.pathname.startsWith(`${APP_PREFIX_PATH}/consumidor`);
	const useComercializadoraShell =
		isComercializador || isOnPainelComercializadora;
	const useParceiroShell = isParceiro || isOnPainelParceiro;

	useEffect(() => {
		if (JwtAuthService.isLogged()) {
			MenuService.populateMenuData().catch(() => {});
		}
	}, []);

	useEffect(() => {
		if (!isComercializador) return;
		if (!isOnPainelComercializadora) {
			history.replace(PAINEL_COMERCIALIZADORA_PATH);
		}
	}, [isComercializador, isOnPainelComercializadora, history]);

	useEffect(() => {
		if (!isParceiro) return;
		if (!isOnPainelParceiro) {
			history.replace(PAINEL_PARCEIRO_PATH);
		}
	}, [isParceiro, isOnPainelParceiro, history]);

	useEffect(() => {
		if (!isInvestidor) return;
		if (!isOnInvestidorArea) {
			history.replace(INVESTIDOR_MARKETPLACE_PATH);
		}
	}, [isInvestidor, isOnInvestidorArea, history]);

	useEffect(() => {
		if (!isVendedor) return;
		if (!isOnVendedorArea) {
			history.replace(VENDEDOR_MEUS_ATIVOS_PATH);
		}
	}, [isVendedor, isOnVendedorArea, history]);

	useEffect(() => {
		if (!isConsumidor) return;
		if (!isOnConsumidorArea) {
			history.replace(CONSUMIDOR_DASHBOARD_PATH);
		}
	}, [isConsumidor, isOnConsumidorArea, history]);

	/** Só após /menu: perfil não é investidor mas permaneceu em rota de investidor (ex.: grupo antigo no Redux) */
	useEffect(() => {
		if (user?.grupo_usuario?.descricao == null) return;
		if (isInvestidor) return;
		if (!isOnInvestidorArea) return;
		history.replace(`${APP_PREFIX_PATH}/dashboard`);
	}, [user?.grupo_usuario?.descricao, isInvestidor, isOnInvestidorArea, history]);

	useEffect(() => {
		if (user?.grupo_usuario?.descricao == null) return;
		if (isVendedor) return;
		if (!isOnVendedorArea) return;
		history.replace(`${APP_PREFIX_PATH}/dashboard`);
	}, [user?.grupo_usuario?.descricao, isVendedor, isOnVendedorArea, history]);

	/** Só após /menu: não comercializadora na rota exclusiva do painel */
	useEffect(() => {
		if (user?.grupo_usuario?.descricao == null) return;
		if (isComercializador) return;
		if (!isOnPainelComercializadora) return;
		history.replace(`${APP_PREFIX_PATH}/dashboard`);
	}, [user?.grupo_usuario?.descricao, isComercializador, isOnPainelComercializadora, history]);

	useEffect(() => {
		if (user?.grupo_usuario?.descricao == null) return;
		if (isParceiro) return;
		if (!isOnPainelParceiro) return;
		history.replace(`${APP_PREFIX_PATH}/dashboard`);
	}, [user?.grupo_usuario?.descricao, isParceiro, isOnPainelParceiro, history]);

	return (
		<IntlProvider
			locale={currentAppLocale.locale}
			messages={currentAppLocale.messages}
		>
			<Loading />
			<ConfigProvider
				locale={currentAppLocale.antd}
				direction={direction}
			>
				<Suspense fallback={<Loading cover="content" />}>
					<Switch>
						<Route exact path="/">
							{isLoggedIn ? (
								<Redirect to={APP_PREFIX_PATH} />
							) : (
								<Redirect to={`${AUTH_PREFIX_PATH}/welcome`} />
							)}
						</Route>
						<Route path="/assinatura">
							<AssinaturaViews />
						</Route>
						<Route path={AUTH_PREFIX_PATH}>
							<AuthLayout direction={direction} />
						</Route>
						<RouteInterceptor
							path={APP_PREFIX_PATH}
							isAuthenticated={token}
						>
							<Content>
								{useComercializadoraShell ? (
									<ComercializadoraViews />
								) : useParceiroShell ? (
									<ParceiroViews />
								) : isOnVendedorArea ? (
									<VendedorViews />
								) : isOnInvestidorArea ? (
									<InvestidorViews />
								) : (isConsumidor || isOnConsumidorArea) ? (
									<ConsumidorViews />
								) : (user?.name ? (
									<AppLayout direction={direction} location={location} />
								) : <></>
								)}
							</Content>
						</RouteInterceptor>
					</Switch>
				</Suspense>
			</ConfigProvider>
		</IntlProvider>
	);
};

const mapStateToProps = ({ theme, auth, user }) => {
	const { locale, direction } = theme;
	const { token } = auth;
	return { locale, token, direction, user };
};

export default withRouter(connect(mapStateToProps)(Views));
